import { Directive, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';

export interface PdfPageRenderedEvent {
  pageNum: number;
  widthPx: number;
  heightPx: number;
}

interface PdfRenderTask {
  promise: Promise<void>;
  cancel(): void;
}

/**
 * Renders one pdf.js page into its host <canvas>, independently of every other
 * page. A stalled byte range for one page only ever affects that page — its
 * spinner keeps running and its neighbours still render.
 *
 * Marks the parent `.pdf-page-wrapper` with `.rendered` (existing CSS hides the
 * Docsila beat-loader) and applies the user-rotation transform to it using the
 * same formula the previous `applyPreviewPageRotation` used.
 */
@Directive({
  selector: 'canvas[appPdfPageCanvas]',
  standalone: true,
})
export class PdfPageCanvasDirective implements OnChanges, OnDestroy {
  @Input() pdfDoc: any = null;
  @Input() pageNum = 0;
  @Input() scale = 1;
  @Input() userRotation = 0;
  @Output() rendered = new EventEmitter<PdfPageRenderedEvent>();

  private renderTask: PdfRenderTask | null = null;
  private token = 0;

  constructor(private readonly host: ElementRef<HTMLCanvasElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    const needsRender = !!(changes['pdfDoc'] || changes['pageNum'] || changes['scale']);
    if (needsRender) {
      void this.render();
      return;
    }
    if (changes['userRotation']) {
      const wrapper = this.host.nativeElement.parentElement;
      if (wrapper) { this.applyRotationTransform(wrapper, true); }
    }
  }

  ngOnDestroy(): void {
    this.token++;
    this.cancel();
    this.clearCanvas();
  }

  private cancel(): void {
    try { this.renderTask?.cancel(); } catch { /* already settled */ }
    this.renderTask = null;
  }

  private clearCanvas(): void {
    const canvas = this.host.nativeElement;
    canvas.width = 0;
    canvas.height = 0;
  }

  private async render(): Promise<void> {
    this.cancel();
    const token = ++this.token;
    const canvas = this.host.nativeElement;
    const wrapper = canvas.parentElement;
    wrapper?.classList.remove('rendered');

    if (!this.pdfDoc || this.pageNum < 1) {
      this.clearCanvas();
      return;
    }

    try {
      const page = await this.pdfDoc.getPage(this.pageNum);
      if (token !== this.token) { return; }

      // Intrinsic /Rotate only. User rotation is a CSS transform on the wrapper so
      // the layout box (and therefore the scroll geometry) never changes.
      const viewport = page.getViewport({ scale: this.scale, rotation: page.rotate || 0 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { return; }

      const task: PdfRenderTask = page.render({ canvasContext: ctx, viewport });
      this.renderTask = task;
      await task.promise;
      if (token !== this.token) { return; }
      this.renderTask = null;

      if (wrapper) {
        wrapper.classList.add('rendered');
        this.applyRotationTransform(wrapper, false);
      }
      this.rendered.emit({ pageNum: this.pageNum, widthPx: viewport.width, heightPx: viewport.height });
    } catch (err: any) {
      if (token !== this.token) { return; }
      if (err?.name === 'RenderingCancelledException') { return; }
    }
  }

  private applyRotationTransform(wrapper: HTMLElement, withTransition: boolean): void {
    const rotation = this.userRotation || 0;
    wrapper.style.transition = withTransition ? 'transform 0.3s ease-in-out' : 'none';
    if (!rotation) {
      wrapper.style.transform = '';
      return;
    }
    let transform = `rotate(${rotation}deg)`;
    const visual = ((rotation % 360) + 360) % 360;
    if (visual === 90 || visual === 270) {
      const canvas = this.host.nativeElement;
      if (canvas.width > 0 && canvas.height > 0) {
        const aspect = canvas.width / canvas.height;
        const scaleFactor = aspect !== 1 ? Math.min(1 / aspect, aspect) : 1;
        transform = `rotate(${rotation}deg) scale(${scaleFactor})`;
      }
    }
    wrapper.style.transform = transform;
    wrapper.style.transformOrigin = 'center center';
  }
}
